import React, { useState, useEffect, useMemo } from 'react';
import { useFirebase } from '../Auth/FirebaseProvider';
import { clientService } from '../../services/clientService';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  InputAdornment,
  Button,
  TablePagination,
  Chip,
  Tooltip,
  SortDirection,
  AlertColor,
  Snackbar,
  Alert
} from '@mui/material';
import { FaSearch, FaPlus, FaUsers, FaEdit } from 'react-icons/fa';
import Loader from '../Common/Loader';
import CustomerEditSidePanel from './CustomerEditSidePanel';
import CustomerCreatePanel from './CustomerCreatePanel';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { SortableTableCell } from '../Common/SortableTableCell';
import EmptyStateView from '../Common/EmptyView';
import { orderService } from '../../services/orderService';

// Interface pour Firestore Timestamp
interface FirestoreTimestamp {
  toDate: () => Date;
}

// Interface pour les clients
interface Client {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  loyaltyPoints: number;
  loyalty?: {
    name: string;
    expirationDate: FirestoreTimestamp | Date;
  };
  orders?: Array<{
    id: string;
    pickupTime: FirestoreTimestamp | Date;
    totalPrice?: number;
    items?: Array<{
      id?: string;
      name?: string;
      quantity?: number;
      category?: string;
    }>;
  }>;
  viewCount?: number;
}

// Interface pour les statistiques des clients
interface ClientStats {
  totalSpent: number;
  orderCount: number;
  lastOrder: Date | null;
  firstOrder: Date | null;
  averageValue: number;
}

// Interface pour le statut de fidélité
interface LoyaltyStatus {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
}

// Interface pour la définition des colonnes
interface Column {
  id: string;
  label: string;
  render?: (client: Client) => React.ReactNode;
}

// Type pour les champs de tri
type SortField = 'fullName' | 'activity' | 'spending' | 'loyaltyPoints' | 'loyaltyStatus';

// Interface pour l'état du snackbar
interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const Customers: React.FC = () => {
  const { foodTruckId, loading: contextLoading } = useFirebase();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [orderBy, setOrderBy] = useState<SortField>('fullName');
  const [order, setOrder] = useState<SortDirection>('asc');
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editedClient, setEditedClient] = useState<Client | null>(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (!foodTruckId || contextLoading) return;
  
    let unsubscribe: (() => void) | undefined;
  
    const initializeService = () => {
      try {
        const clientSrv = clientService.getInstance(foodTruckId);
        return clientSrv;
      } catch (error) {
        console.error("Error initializing client service:", error);
        setError((error as Error).message);
        return null;
      }
    };
  
    const loadClients = async () => {
      try {
        setLoading(true);
        const service = initializeService();
        if (!service) return;
  
        // Typez correctement la fonction listenClients pour qu'elle retourne une fonction de désabonnement
        unsubscribe = service.listenClients(
          async (clientData: Client[]) => {
            // Récupérer les commandes pour chaque client
            const ordersService = orderService.getInstance(foodTruckId);
            const clientsWithOrders = await Promise.all(
              clientData.map(async (client) => {
                try {
                  const orders = await ordersService.getClientOrders(client.id);
                  return { ...client, orders };
                } catch (error) {
                  console.error(`Error fetching orders for client ${client.id}:`, error);
                  return { ...client, orders: [] };
                }
              })
            );
  
            setClients(clientsWithOrders);
            setFilteredClients(clientsWithOrders);
            setError(null);
            setLoading(false);
          },
          (error: Error) => {
            console.error("Error fetching clients:", error);
            setError("Erreur lors du chargement des clients");
            setLoading(false);
          }
        ) as () => void;
      } catch (error) {
        console.error("Error loading clients:", error);
        setError("Erreur lors du chargement des clients");
        setLoading(false);
      }
    };
  
    loadClients();
  
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [foodTruckId, contextLoading]);
  

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.firstName?.toLowerCase().includes(value.toLowerCase()) ||
        client.lastName?.toLowerCase().includes(value.toLowerCase()) ||
        client.email?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
    }
    setPage(0); // Reset to first page when searching
  };

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting
  };

  const handleClientSelect = (client: Client) => {
    setEditedClient({ ...client });
    setIsSidePanelOpen(true);
  };

  // Helper function to safely convert any timestamp-like object to a Date
  const toDate = (date: FirestoreTimestamp | Date | null | undefined): Date | null => {
    if (!date) return null;
    
    // Check if it's a Firestore timestamp
    if (typeof (date as FirestoreTimestamp).toDate === 'function') {
      return (date as FirestoreTimestamp).toDate();
    } 
    // It's already a Date
    return date as Date;
  };

  // Format days since date
  const formatDaysSince = (date: FirestoreTimestamp | Date | null | undefined): string => {
    if (!date) return "N/A";
    
    const dateObj = toDate(date);
    if (!dateObj) return "N/A";
    
    const days = Math.ceil((new Date().getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));
    
    if (days < 1) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 30) return `il y a ${days} jours`;
    if (days < 365) {
      const months = Math.floor(days / 30);
      return `il y a ${months} mois`;
    }
    const years = Math.floor(days / 365);
    return `il y a ${years} an${years > 1 ? 's' : ''}`;
  };

  const getLoyaltyStatus = (client: Client): LoyaltyStatus => {
    if (!client.loyalty) {
      return {
        label: 'Aucun abonnement',
        color: 'default'
      };
    }

    const expirationDate = toDate(client.loyalty.expirationDate);
    const now = new Date();

    if (!expirationDate) {
      return {
        label: 'Statut inconnu',
        color: 'default'
      };
    }

    if (expirationDate < now) {
      return {
        label: 'Expiré',
        color: 'error'
      };
    }

    // Alerte si expiration dans moins de 30 jours
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiration <= 30) {
      return {
        label: `Expire dans ${daysUntilExpiration}j`,
        color: 'warning'
      };
    }

    return {
      label: client.loyalty.name,
      color: 'success'
    };
  };

  // Calculer les statistiques de base par client
  const getClientStats = (client: Client): ClientStats => {
    const orders = client.orders || [];
    if (!orders.length) return { 
      totalSpent: 0, 
      orderCount: 0, 
      lastOrder: null, 
      firstOrder: null,
      averageValue: 0 
    };
    
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = toDate(a.pickupTime);
      const dateB = toDate(b.pickupTime);
      
      if (!dateA || !dateB) return 0;
      return dateB.getTime() - dateA.getTime();
    });
    
    const totalSpent = orders.reduce((total, order) => total + (order.totalPrice || 0), 0);
    const lastOrderDate = sortedOrders.length > 0 ? toDate(sortedOrders[0].pickupTime) : null;
    const firstOrderDate = sortedOrders.length > 0 ? toDate(sortedOrders[sortedOrders.length - 1].pickupTime) : null;
    
    return {
      totalSpent,
      orderCount: orders.length,
      lastOrder: lastOrderDate,
      firstOrder: firstOrderDate,
      averageValue: totalSpent / orders.length
    };
  };

  const columns: Column[] = [
    { 
      id: 'fullName',
      label: 'Client', 
      render: (client: Client) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" fontWeight="medium">
            {client.lastName} {client.firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {client.email}
          </Typography>
        </Box>
      )
    },
    { 
      id: 'activity', 
      label: 'Activité',
      render: (client: Client) => {
        const stats = getClientStats(client);
        return stats.orderCount > 0 ? (
          <Tooltip title={`Première commande: ${stats.firstOrder ? formatDate(stats.firstOrder) : 'N/A'}`}>
            <Box>
              <Typography variant="body2">
                {stats.orderCount} commande{stats.orderCount > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Dernière: {formatDaysSince(stats.lastOrder)}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">Aucune commande</Typography>
        );
      }
    },
    { 
      id: 'spending', 
      label: 'Dépenses',
      render: (client: Client) => {
        const stats = getClientStats(client);
        return stats.totalSpent > 0 ? (
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {formatCurrency(stats.totalSpent)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Moy: {formatCurrency(stats.averageValue)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        );
      }
    },
    { 
      id: 'loyaltyPoints', 
      label: 'Fidélité',
      render: (client: Client) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1">
            {client.loyaltyPoints || 0} pts
          </Typography>
        </Box>
      )
    },
    {
      id: 'loyaltyStatus',
      label: 'Abonnement',
      render: (client: Client) => {
        const status = getLoyaltyStatus(client);
        return (
          <Chip
            label={status.label}
            color={status.color}
            size="small"
            sx={{ minWidth: '120px' }}
          />
        );
      }
    }
  ];

  const sortedClients = useMemo(() => {
    return [...filteredClients].sort((a, b) => {
      switch (orderBy) {
        case 'fullName':
          const aName = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
          const bName = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
          return order === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
          
        case 'activity':
          const aStats = getClientStats(a);
          const bStats = getClientStats(b);
          return order === 'asc' 
            ? aStats.orderCount - bStats.orderCount
            : bStats.orderCount - aStats.orderCount;
            
        case 'spending':
          const aSpending = getClientStats(a).totalSpent;
          const bSpending = getClientStats(b).totalSpent;
          return order === 'asc' ? aSpending - bSpending : bSpending - aSpending;
          
        case 'loyaltyPoints':
          return order === 'asc' 
            ? (a.loyaltyPoints || 0) - (b.loyaltyPoints || 0)
            : (b.loyaltyPoints || 0) - (a.loyaltyPoints || 0);
          
        case 'loyaltyStatus':
          const aStatus = getLoyaltyStatus(a).label;
          const bStatus = getLoyaltyStatus(b).label;
          return order === 'asc' ? aStatus.localeCompare(bStatus) : bStatus.localeCompare(aStatus);
          
        default:
          return 0;
      }
    });
  }, [filteredClients, order, orderBy]);

  const paginatedClients = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedClients.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedClients, page, rowsPerPage]);

  if (loading) return <Loader />;
  if (error) return <Typography color="error">{error}</Typography>;

  const hasAllClients = clients.length > 0;
  const hasFilteredResults = filteredClients.length > 0;

  return (
    <Box sx={{ p: 0 }}>
      {/* Header de la page */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 3,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaUsers size={20} color="white" />
              </Box>
              Gestion des clients
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              Suivez l'activité et la fidélité de vos clients
            </Typography>
          </Box>
          {hasAllClients ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaPlus />}
              onClick={() => setIsCreatePanelOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Ajouter un client
            </Button>
          ) : <Box />}
        </Box>
      </Box>

      {/* Contenu principal */}
      <Box sx={{ px: 3 }}>

        {hasAllClients ? (
          <>
            {/* Barre de recherche */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                mb: 3
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher un client..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>

            {hasFilteredResults ? (
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <SortableTableCell
                          key={column.id}
                          field={column.id as SortField}
                          label={column.label}
                          currentSort={orderBy}
                          currentOrder={order}
                          onSort={handleRequestSort}
                        />
                      ))}
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedClients.map((client) => (
                      <TableRow key={client.id} hover>
                        {columns.map((column) => (
                          <TableCell key={column.id}>
                            {column.render ? column.render(client) : (
                              client[column.id as keyof Client] as React.ReactNode
                            )}
                          </TableCell>
                        ))}
                        <TableCell>
                          <IconButton
                            onClick={() => handleClientSelect(client)}
                            size="small"
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': { 
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            <FaEdit size={14} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredClients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Lignes par page"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              />
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Aucun client ne correspond à votre recherche
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 2 }}
                >
                  Réinitialiser la recherche
                </Button>
              </Paper>
            )}
          </>
        ) : (
          <EmptyStateView
            icon={<FaUsers size={60} />}
            title="Aucun client disponible"
            description="Vous n'avez pas encore de clients dans votre base de données. Ajoutez vos premiers clients pour suivre leur historique d'achat et leur fidélité."
            buttonText="Ajouter un client"
            onAction={() => setIsCreatePanelOpen(true)}
          />
        )}
      </Box>

      <Drawer
        anchor="right"
        open={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
      >
        <CustomerEditSidePanel
          customerId={editedClient?.id}
          foodTruckId={foodTruckId}
          onClose={() => setIsSidePanelOpen(false)}
          onSuccess={() => {
            setIsSidePanelOpen(false);
            setSnackbar({
              open: true,
              message: 'Client mis à jour avec succès',
              severity: 'success'
            });
          }}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
      >
        <CustomerCreatePanel
          foodTruckId={foodTruckId}
          onClose={() => setIsCreatePanelOpen(false)}
          onSuccess={() => {
            setIsCreatePanelOpen(false);
            setSnackbar({
              open: true,
              message: 'Client ajouté avec succès',
              severity: 'success'
            });
          }}
        />
      </Drawer>
      <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
    
  );
};

export default Customers;